/**
 * Actions: everything the platform can do, and what stands between an actor
 * and doing it.
 *
 * The odd one out among the configuration screens, and deliberately so. An
 * action is not something you switch — it is what a feature lets you do — so
 * there is nothing to save here: no subject, no draft, no Save bar. It is a
 * reference table, and the thing it answers is "why can't they do that?".
 *
 * It needs no endpoint either. ACTIONS is a shared catalogue compiled into
 * both sides, so the screen reads it directly rather than asking the server
 * to send back a constant.
 */

import { ACTIONS, type Action } from "@wallyo/contracts/actions";
import { PERMISSIONS } from "@wallyo/contracts/permissions";
import { useEffect, useMemo, useState } from "react";

import { actionName, customerFeatureName, featureName, permissionName, t } from "../../i18n";
import { PageTop } from "../../shell/PageTop";
import { listDirectory, listRoles, listStaffActions, updateActionEnablements } from "../../api/console-api";
import type { StaffRoleListItem } from "@wallyo/contracts/console";
import { useApi } from "../../api/context";
import { useAlerts } from "../../shell/alerts";
import { ActionsByPerson, type ActionsState } from "./ActionsByPerson";
import { SaveBar } from "../features/FeatureTableView";
import { useIntentWrite } from "../team/TeamDialogs";
import type { StaffActionItem } from "@wallyo/contracts/console/hr";

type Surface = "app" | "console" | "system";

const SURFACE_OF: Record<Action["actorType"], Surface> = {
  customer: "app",
  staff: "console",
  system: "system",
};

/** The permission covering an action, when one does (ADR-0019). */
const PERMISSION_FOR = new Map<string, string>(
  PERMISSIONS.flatMap((permission) =>
    permission.actions.map((actionId) => [actionId as string, permission.settingId as string]),
  ),
);

/** How many live actions each feature gates: what else moving it would take. */
const GATED_BY = new Map<string, number>();
for (const action of ACTIONS) {
  if (action.status === "retired" || !action.requiredFeature) continue;
  const id = action.requiredFeature as string;
  GATED_BY.set(id, (GATED_BY.get(id) ?? 0) + 1);
}

type Gate = { label: string; href: string; alsoGates: number };

/**
 * What stands between an actor and this action, and where to go and change
 * it. Nothing means anyone who can act at all may take it.
 *
 * An action is not a switch of its own: the feature is what gets granted, and
 * one feature gates several actions. So the row links to whatever actually
 * controls it and says how much else moves with it — which is the thing you
 * need to know before you move it.
 */
function gatesOf(action: Action): readonly Gate[] {
  const gates: Gate[] = [];
  if (action.requiredFeature) {
    // A customer action is gated by a customer feature, which lives in a
    // different catalogue from the staff one. Asking the wrong one falls back
    // to the id, and an id is for the code, not the screen.
    const id = action.requiredFeature as string;
    const customer = id.endsWith("_USER_FEATURE_ENABLEMENT");
    gates.push({
      label: customer ? customerFeatureName(id as never) : featureName(id as never),
      href: customer ? `#features/${id}` : "#team",
      alsoGates: (GATED_BY.get(id) ?? 1) - 1,
    });
  }
  const permission = PERMISSION_FOR.get(action.actionId);
  if (permission) gates.push({ label: permissionName(permission as never), href: "#team", alsoGates: 0 });
  return gates;
}

export function ActionsScreen() {
  const [surface, setSurface] = useState<Surface>("app");
  const [search, setSearch] = useState("");
  // Two ways in, like Feature Enablements: the catalogue, and one person.
  const [view, setView] = useState<ActionsView>("catalogue");

  const live = useMemo(() => ACTIONS.filter((action) => action.status !== "retired"), []);
  const counts = useMemo(() => {
    const tally: Record<Surface, number> = { app: 0, console: 0, system: 0 };
    for (const action of live) tally[SURFACE_OF[action.actorType]] += 1;
    return tally;
  }, [live]);

  const shown = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return live
      .filter((action) => SURFACE_OF[action.actorType] === surface)
      .filter((action) => needle === "" || actionName(action.actionId).toLocaleLowerCase().includes(needle))
      .slice()
      .sort((a, b) => a.area.localeCompare(b.area) || actionName(a.actionId).localeCompare(actionName(b.actionId)));
  }, [live, surface, search]);

  if (view !== "catalogue") {
    return (
      <>
        <PageTop title={t("actions.title")} />
        <ViewTabs view={view} onView={setView} />
        {view === "person" ? <ActionsByPersonTab /> : <ActionsByRoleTab />}
      </>
    );
  }

  return (
    <>
      <PageTop title={t("actions.title")} />
      <ViewTabs view={view} onView={setView} />
      <div className="surface-tabs" role="tablist">
        {(["app", "console", "system"] as const).map((each) => (
          <button
            key={each}
            type="button"
            role="tab"
            aria-selected={surface === each}
            className={surface === each ? "surface-tab on" : "surface-tab"}
            onClick={() => setSurface(each)}
          >
            {t(each === "system" ? "actions.surface.system" : (`features.surface.${each}` as const))}{" "}
            <span className="surface-count">{counts[each]}</span>
          </button>
        ))}
      </div>

      <div className="filter-chips">
        <label className="chip chip-search">
          <span>{t("features.filter.search")}</span>
          <input
            type="search"
            value={search}
            placeholder={t("actions.search")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      <div className="card">
        <table className="config-list feature-table">
          <thead>
            <tr>
              <th>{t("actions.col.action")}</th>
              <th>{t("actions.col.area")}</th>
              <th>{t("actions.col.effect")}</th>
              <th>{t("actions.col.audited")}</th>
              <th>{t("actions.col.needs")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((action) => {
              const gates = gatesOf(action);
              return (
                <tr key={action.actionId}>
                  <td>
                    {actionName(action.actionId)}
                    {action.status === "planned" ? <span className="pill quiet">{t("features.planned")}</span> : null}
                  </td>
                  <td>{t(`area.${action.area}` as const)}</td>
                  <td>{t(`actions.effect.${action.effect}` as const)}</td>
                  <td>{t(action.effect === "read" && !action.sensitive ? "actions.no" : "actions.yes")}</td>
                  <td>
                    {gates.length === 0 ? (
                      <span className="muted">{t("actions.needs.nothing")}</span>
                    ) : (
                      gates.map((gate) => (
                        <a key={gate.label} className="pill quiet gate" href={gate.href}>
                          {gate.label}
                          {gate.alsoGates > 0 ? (
                            <span className="gate-also">{t("actions.alsoGates", { count: gate.alsoGates })}</span>
                          ) : null}
                        </a>
                      ))
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {shown.length === 0 ? <div className="empty">{t("actions.none")}</div> : null}
      </div>
    </>
  );
}

/**
 * The switches moved but not yet saved.
 *
 * Feature Enablements collects changes into a draft and commits them in one
 * Save; this does the same, because four toggles should be one transaction
 * with one reason, and because somebody should see what they are about to do
 * before it happens rather than after each click.
 */
type ActionDraft = Readonly<Record<string, boolean | null>>;

/** What a row shows: the draft's answer when it has one, else the server's. */
export function shownValue(action: StaffActionItem, draft: ActionDraft): boolean {
  const pending = draft[action.actionId];
  if (pending === undefined) return action.value;
  // Cleared goes back to what they inherit, which this screen cannot know
  // until the server answers — so it shows the inherited value it has.
  return pending === null ? action.value : pending;
}

export function changed(action: StaffActionItem, draft: ActionDraft): boolean {
  return draft[action.actionId] !== undefined;
}

function useActionDraft() {
  const [draft, setDraft] = useState<ActionDraft>({});
  return {
    draft,
    count: Object.keys(draft).length,
    discard: () => setDraft({}),
    /** Setting a row back to what the server says removes it from the draft. */
    move: (action: StaffActionItem, value: boolean | null) =>
      setDraft((current) => {
        const next = { ...current };
        if (value !== null && value === action.value) delete next[action.actionId];
        else next[action.actionId] = value;
        return next;
      }),
    changes: (actions: readonly StaffActionItem[]) =>
      Object.entries(draft).map(([actionId, value]) => {
        const action = actions.find((each) => each.actionId === actionId);
        return { actionId, value, ...(action?.source === "staffMember" ? {} : {}) };
      }),
  };
}

type ActionsView = "catalogue" | "person" | "role";

const VIEW_LABEL = {
  catalogue: "actions.view.catalogue",
  person: "actions.view.person",
  role: "actions.view.role",
} as const;

function ViewTabs({ view, onView }: { view: ActionsView; onView: (next: ActionsView) => void }) {
  return (
    <div className="tabs payment-tabs" role="tablist" aria-label={t("actions.title")}>
      {(["catalogue", "person", "role"] as const).map((each) => (
        <button
          key={each}
          type="button"
          role="tab"
          aria-selected={view === each}
          className={view === each ? "tab on" : "tab"}
          onClick={() => onView(each)}
        >
          {t(VIEW_LABEL[each])}
        </button>
      ))}
    </div>
  );
}

/**
 * Search somebody, see every action, and where each answer came from.
 *
 * The toggles are not wired yet: switching one writes the feature at this
 * person's scope and, where the action needs one, the permission — two writes
 * that exist but are not joined behind one control. Until they are, the switch
 * says so rather than moving and doing nothing.
 */
function ActionsByPersonTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<ActionsState>({ status: "idle" });
  const edit = useActionDraft();
  const save = useIntentWrite();
  const [reason, setReason] = useState("");
  // More than one person matched a name: they pick, rather than the screen
  // guessing which one they meant.
  const [matches, setMatches] = useState<readonly { uuid: number; name: string; roles: readonly string[] }[]>([]);

  const open = (uuid: number) => {
    setMatches([]);
    setState({ status: "loading" });
    void listStaffActions(client, { uuid }).then(
      (data) => setState({ status: "ready", data }),
      (error: unknown) => setState({ status: "failed", error }),
    );
  };

  /**
   * A number is somebody's badge and opens them. Anything else is a name, and
   * the directory answers it — because nobody remembers eight digits, and
   * making them look the number up first is making them use two screens.
   */
  const search = () => {
    const typed = query.trim();
    if (typed === "") return;
    if (/^\d+$/.test(typed)) {
      open(Number(typed));
      return;
    }
    setState({ status: "loading" });
    void listDirectory(client, typed).then(
      ({ people }) => {
        if (people.length === 1) {
          open(people[0].uuid);
          return;
        }
        setMatches(people);
        setState({ status: "idle" });
        if (people.length === 0) alerts.refuse(t("directory.none", { query: typed }));
      },
      (error: unknown) => setState({ status: "failed", error }),
    );
  };

  const data = state.status === "ready" ? state.data : null;

  return (
    <>
      <ActionsByPerson
        state={state}
        query={query}
        onQuery={setQuery}
        onSearch={search}
        matches={matches}
        onPick={open}
        draft={edit.draft}
        onToggle={(action) => edit.move(action, !shownValue(action, edit.draft))}
        onClear={(action) => edit.move(action, null)}
      />
      <SaveBar
        count={edit.count}
        reason={reason}
        pending={save.pending}
        problem={save.problem}
        onReason={(next) => {
          save.edited();
          setReason(next);
        }}
        onDiscard={edit.discard}
        onSave={() =>
          data
            ? void save.run(
                reason.trim().length === 0 ? t("error.reason.no_reason") : null,
                (idempotencyKey) =>
                  updateActionEnablements(
                    client,
                    {
                      subject: { uuid: (data.subject as { uuid: number }).uuid },
                      changes: edit.changes(data.actions),
                      reason: reason.trim(),
                    },
                    { idempotencyKey },
                  ),
                (next) => {
                  edit.discard();
                  setReason("");
                  alerts.ok(t("actions.saved", { count: String(edit.count) }));
                  setState({ status: "ready", data: next });
                },
              )
            : undefined
        }
      />
    </>
  );
}

/**
 * The same table, asked of a role.
 *
 * A role has no resolved answer the way a person does — it grants or it does
 * not — so there is no override to clear and no inheritance to name. The
 * picker replaces the search box, because a role is chosen from a short list
 * rather than looked up by a number.
 */
function ActionsByRoleTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [staffRoleId, setStaffRoleId] = useState("");
  const [roles, setRoles] = useState<readonly StaffRoleListItem[]>([]);
  const [state, setState] = useState<ActionsState>({ status: "idle" });
  const edit = useActionDraft();
  const save = useIntentWrite();
  const [reason, setReason] = useState("");

  useEffect(() => {
    let current = true;
    void listRoles(client).then(
      (data) => current && setRoles(data.roles),
      () => current && setRoles([]),
    );
    return () => {
      current = false;
    };
  }, [client]);

  useEffect(() => {
    if (staffRoleId === "") {
      setState({ status: "idle" });
      return;
    }
    let current = true;
    setState({ status: "loading" });
    void listStaffActions(client, { staffRoleId }).then(
      (data) => current && setState({ status: "ready", data }),
      (error: unknown) => current && setState({ status: "failed", error }),
    );
    return () => {
      current = false;
    };
  }, [client, staffRoleId]);

  const data = state.status === "ready" ? state.data : null;

  return (
    <>
      <ActionsByPerson
        state={state}
        picker={
          <label className="field">
            <span className="label">{t("actions.pickRole")}</span>
            <select value={staffRoleId} onChange={(event) => setStaffRoleId(event.currentTarget.value)}>
              <option value="">—</option>
              {roles.map((role) => (
                <option key={role.staffRoleId} value={role.staffRoleId}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>
        }
        draft={edit.draft}
        onToggle={(action) => edit.move(action, !shownValue(action, edit.draft))}
        onClear={(action) => edit.move(action, null)}
      />
      <SaveBar
        count={edit.count}
        reason={reason}
        pending={save.pending}
        problem={save.problem}
        onReason={(next) => {
          save.edited();
          setReason(next);
        }}
        onDiscard={edit.discard}
        onSave={() =>
          data
            ? void save.run(
                reason.trim().length === 0 ? t("error.reason.no_reason") : null,
                (idempotencyKey) =>
                  updateActionEnablements(
                    client,
                    { subject: { staffRoleId }, changes: edit.changes(data.actions), reason: reason.trim() },
                    { idempotencyKey },
                  ),
                (next) => {
                  edit.discard();
                  setReason("");
                  alerts.ok(t("actions.saved", { count: String(edit.count) }));
                  setState({ status: "ready", data: next });
                },
              )
            : undefined
        }
      />
    </>
  );
}
